// Drag-and-drop for the page slot boxes (docs/DRAG_DROP_SLOTS_SCOPE.md): the INTERACTION only.
//
// What a drop MEANS lives in slotReorder.ts (pure, tested); what happens to the data, the boxes and the bubbles lives in
// clusterService.ts / pagesModel.ts behind the host's `commit`. This file owns the pointer: the threshold that separates a click from a
// drag, ONE ghost and ONE highlight that are repositioned (never removed and re-created while the pointer moves), and the cleanup.
//
// Performance rules this follows (CLAUDE.md "Performance gotchas"): pointer moves are coalesced to one update per animation frame; the
// ghost moves by `transform` only; the highlight is one persistent rect that is repositioned; there are no CSS filters; and the
// simulation, the page data and the network are untouched until the DROP.
import { planMove, rejectMessage, HOME_SLOT, MAX_PAGE_SLOT } from "./slotReorder";
import type { MovePlan, SlotChange, SlotPage } from "./slotReorder";

const SVG_NS = "http://www.w3.org/2000/svg";

// A press that moves less than this is a CLICK (zoom, drawer, the label link keep working); past it, a drag begins.
export const DRAG_THRESHOLD_PX = 6;

const ACCENT = "#0b506e"; // the app's own live blue (the metrics bar, the live drawer header)
const REFUSED = "#9aa5ab";
const MUTED_TEXT = "#6B7280";
const REMOVE_INK = "#b0463a"; // a muted brick red: it says "this takes it away" without shouting
/** the hint is faint while the pointer is approaching the strip, and plain once it is in it */
export const REMOVE_NEAR_PX = 90;
const REMOVE_HINT_NEAR_OPACITY = "0.35";
const REMOVE_HINT_IN_OPACITY = "1";

export interface SlotBox {
  slot: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface SlotDragHost {
  svgNode(): SVGSVGElement | null;
  // the page slots (0..11) in SVG user units
  boxes(): SlotBox[];
  // the pages as the page model holds them
  pages(): SlotPage[];
  // false in playback, in the single view, in a page's zoom: anywhere a reorder makes no sense
  allowed(): boolean;
  // the page name written in a slot (for the ghost)
  label(slot: number): string;
  // ONE valid drop
  commit(changes: SlotChange[], kind: "move" | "swap"): void;
  // DRAG TO REMOVE (Chris, 2026-10-23): the strip at the bottom of the grey stage, BELOW every box (SVG units), where letting go takes the page out of its slot. Absent or null = no such drop.
  removeZone?(): { x: number; y: number; width: number; height: number } | null;
  // the page in `slot` was dropped in the zone: take it off the boxes (the host does what the Pages panel's REMOVE does, and says so)
  remove?(slot: number): void;
  onDragStart?(): void;
  onDragEnd?(): void;
}

interface DragState {
  pointerId: number | undefined;
  startX: number;
  startY: number;
  lastX: number;
  lastY: number;
  slot: number;
  dragging: boolean;
  boxes: SlotBox[];
  offsetX: number;
  offsetY: number;
  targetSlot: number | null;
  plan: MovePlan | null;
  framePending: boolean;
  frameId: number | null;
  originRect: SVGElement | null;
  originOpacity: string;
  zone: { x: number; y: number; width: number; height: number } | null;
  inRemove: boolean;
}

let host: SlotDragHost | null = null;
let attachedTo: SVGSVGElement | null = null;
let detachListeners: (() => void) | null = null;
let state: DragState | null = null;

// the persistent drag layer, one per svg
let layer: SVGGElement | null = null;
let highlight: SVGRectElement | null = null;
let ghost: SVGGElement | null = null;
let ghostRect: SVGRectElement | null = null;
let ghostLabel: SVGTextElement | null = null;
let ghostReason: SVGTextElement | null = null;
let removeHint: SVGGElement | null = null;
let removeLine: SVGLineElement | null = null;
let removeIcon: SVGGElement | null = null;
let removeText: SVGTextElement | null = null;
let bodyUserSelect = "";

export const isSlotDragActive = (): boolean => !!state && state.dragging;

// Would a drag from this (assigned) slot work right now? The hover hint asks, so it only promises "drag to reorder" where dragging works:
// not on the homepage's box, not in the single view or a page's zoom.
export const slotIsDraggable = (slot: number): boolean => !!host && canDrag(slot, true);

const toSvgPoint = (node: SVGSVGElement, x: number, y: number) => {
  try {
    const matrix = node.getScreenCTM?.();

    if (matrix && node.createSVGPoint) {
      const point = node.createSVGPoint();

      point.x = x;
      point.y = y;
      const mapped = point.matrixTransform(matrix.inverse());

      return { x: mapped.x, y: mapped.y };
    }
  } catch {
    // fall through to client coordinates (also what a test DOM without SVG geometry gets)
  }

  return { x, y };
};

const el = (name: string, attrs: Record<string, string>) => {
  const node = document.createElementNS(SVG_NS, name);

  Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, value));

  return node;
};

const ensureLayer = (svgNode: SVGSVGElement) => {
  if (layer && layer.parentNode === svgNode) {
    return;
  }

  layer = el("g", { class: "slot-drag-layer", "data-slot-drag": "1", "pointer-events": "none" }) as SVGGElement;
  highlight = el("rect", { rx: "4", fill: "none", "stroke-width": "2", display: "none" }) as SVGRectElement;
  ghost = el("g", { display: "none" }) as SVGGElement;
  ghostRect = el("rect", { rx: "4", fill: "#fff", "fill-opacity": "0.88", stroke: ACCENT, "stroke-width": "2" }) as SVGRectElement;
  ghostLabel = el("text", { "text-anchor": "middle", "font-size": "10", fill: "#1E2434" }) as SVGTextElement;
  ghostReason = el("text", { "text-anchor": "middle", "font-size": "10", fill: MUTED_TEXT }) as SVGTextElement;
  ghost.append(ghostRect, ghostLabel, ghostReason);
  // the "drop to remove" hint: one persistent group, only its opacity changes (a fade, no filters, nothing recreated while the pointer moves). It comes AFTER the highlight and the ghost.
  removeHint = el("g", { class: "slot-remove-hint", display: "none", opacity: "0", style: "transition: opacity 0.18s ease;" }) as SVGGElement;
  removeLine = el("line", { stroke: REMOVE_INK, "stroke-opacity": "0.35", "stroke-width": "1", "stroke-dasharray": "5 5" }) as SVGLineElement;
  removeIcon = el("g", { fill: "none", stroke: REMOVE_INK, "stroke-width": "1.4", "stroke-linecap": "round" }) as SVGGElement;
  removeIcon.append(el("circle", { r: "7", cx: "0", cy: "0" }), el("path", { d: "M -2.6 -2.6 L 2.6 2.6 M 2.6 -2.6 L -2.6 2.6" }));
  removeText = el("text", { "text-anchor": "start", "font-size": "12", fill: REMOVE_INK, "font-weight": "500" }) as SVGTextElement;
  removeText.textContent = "Drop to remove";
  removeHint.append(removeLine, removeIcon, removeText);
  layer.append(highlight, ghost, removeHint);
  svgNode.appendChild(layer);
};

const setRemoveHint = (opacity: "0" | typeof REMOVE_HINT_NEAR_OPACITY | typeof REMOVE_HINT_IN_OPACITY) => {
  if (!removeHint) {
    return;
  }

  removeHint.setAttribute("display", opacity === "0" ? "none" : "");
  removeHint.setAttribute("opacity", opacity);
};

const placeRemoveHint = (zone: { x: number; y: number; width: number; height: number }) => {
  if (!removeLine || !removeIcon || !removeText) {
    return;
  }

  const middleY = zone.y + zone.height / 2;
  const centreX = zone.x + zone.width / 2;

  removeLine.setAttribute("x1", String(zone.x + 12));
  removeLine.setAttribute("x2", String(zone.x + zone.width - 12));
  removeLine.setAttribute("y1", String(zone.y));
  removeLine.setAttribute("y2", String(zone.y));
  removeIcon.setAttribute("transform", `translate(${(centreX - 62).toFixed(1)} ${middleY.toFixed(1)})`);
  removeText.setAttribute("x", String(centreX - 48));
  removeText.setAttribute("y", String(middleY + 4));
};

const hideLayer = () => {
  highlight?.setAttribute("display", "none");
  ghost?.setAttribute("display", "none");
  setRemoveHint("0");

  if (ghostReason) {
    ghostReason.textContent = "";
  }
};

const rectOf = (target: EventTarget | null) => {
  const rect = (target as Element | null)?.closest?.("rect[id^='rect_']") as SVGElement | null;

  if (!rect) {
    return null;
  }

  const slot = Number(rect.getAttribute("rectIndex"));

  return Number.isInteger(slot) ? { rect, slot, selected: rect.getAttribute("selected") === "true" } : null;
};

// Slot 0 is the homepage's and "All other pages" (12) is not a page slot: neither starts a drag.
const canDrag = (slot: number, selected: boolean) =>
  !!host && selected && slot > HOME_SLOT && slot <= MAX_PAGE_SLOT && host.allowed();

const paintTarget = () => {
  if (!state || !highlight || !ghostRect || !ghostReason) {
    return;
  }

  const { plan, targetSlot } = state;
  const box = state.boxes.find((b) => b.slot === targetSlot);

  if (!box || !plan || plan.kind === "none") {
    highlight.setAttribute("display", "none");
    ghostRect.setAttribute("stroke", ACCENT);
    ghostRect.removeAttribute("stroke-dasharray");
    ghostReason.textContent = "";

    return;
  }

  const valid = plan.kind === "move" || plan.kind === "swap";

  highlight.setAttribute("x", String(box.x - 3));
  highlight.setAttribute("y", String(box.y - 3));
  highlight.setAttribute("width", String(box.width + 6));
  highlight.setAttribute("height", String(box.height + 6));
  highlight.setAttribute("stroke", valid ? ACCENT : REFUSED);
  highlight.setAttribute("fill", valid ? ACCENT : "none");
  highlight.setAttribute("fill-opacity", valid ? "0.06" : "0");

  if (valid) {
    highlight.removeAttribute("stroke-dasharray");
    ghostRect.setAttribute("stroke", ACCENT);
    ghostRect.removeAttribute("stroke-dasharray");
    ghostReason.textContent = "";
  } else {
    highlight.setAttribute("stroke-dasharray", "5 4");
    ghostRect.setAttribute("stroke", REFUSED);
    ghostRect.setAttribute("stroke-dasharray", "5 4");
    ghostReason.textContent = plan.kind === "reject" ? rejectMessage(plan.reason) : "";
  }

  highlight.setAttribute("display", "");
};

const update = () => {
  if (!state || !state.dragging || !host) {
    return;
  }

  state.framePending = false;
  state.frameId = null;

  const svgNode = attachedTo;

  if (!svgNode || !ghost) {
    return;
  }

  const point = toSvgPoint(svgNode, state.lastX, state.lastY);

  ghost.setAttribute("transform", `translate(${(point.x - state.offsetX).toFixed(1)} ${(point.y - state.offsetY).toFixed(1)})`);

  // DRAG TO REMOVE: in the strip below the boxes the drop means "take this page off the boxes", and no box is a target there. Near it the hint is only a whisper; in it, plain.
  const zone = state.zone;
  const inZone = !!zone && point.y >= zone.y && point.x >= zone.x && point.x <= zone.x + zone.width;

  if (zone) {
    setRemoveHint(inZone ? REMOVE_HINT_IN_OPACITY : point.y >= zone.y - REMOVE_NEAR_PX ? REMOVE_HINT_NEAR_OPACITY : "0");
  }

  if (inZone !== state.inRemove) {
    state.inRemove = inZone;
    ghostRect?.setAttribute("stroke", inZone ? REMOVE_INK : ACCENT);
    // in the strip the dragged box fades back (its fill and its name) so the hint under it reads cleanly instead of colliding with the page's name
    ghostRect?.setAttribute("fill-opacity", inZone ? "0.3" : "0.88");
    ghostLabel?.setAttribute("opacity", inZone ? "0.2" : "1");

    if (inZone) {
      ghostRect?.removeAttribute("stroke-dasharray");
    }
  }

  if (inZone) {
    if (state.targetSlot !== null) {
      state.targetSlot = null;
      state.plan = null;
      paintTarget();
    }

    return;
  }

  const target =
    state.boxes.find(
      (b) => point.x >= b.x && point.x <= b.x + b.width && point.y >= b.y && point.y <= b.y + b.height,
    )?.slot ?? null;

  if (target === state.targetSlot) {
    return;
  }

  state.targetSlot = target;

  if (target === null) {
    state.plan = null;
  } else {
    state.plan = planMove(host.pages(), state.slot, target);
  }

  paintTarget();
};

// One update per animation frame. `framePending` is set BEFORE asking for the frame and cleared by update itself, so the order of "the
// callback ran" and "the id came back" cannot matter (a synchronous requestAnimationFrame, as a test may supply, must not leave it stuck).
const schedule = () => {
  if (!state || state.framePending) {
    return;
  }

  state.framePending = true;

  const id = requestAnimationFrame(update);

  if (state && state.framePending) {
    state.frameId = id;
  }
};

// stops the click that follows a drag's pointerup from also zooming the box
const swallowNextClick = () => {
  const node = attachedTo;

  if (!node) {
    return;
  }

  const stop = (event: Event) => {
    event.stopPropagation();
    event.preventDefault();
  };

  node.addEventListener("click", stop, { capture: true, once: true });
  window.setTimeout(() => node.removeEventListener("click", stop, true), 80);
};

const end = () => {
  const current = state;

  state = null;
  window.removeEventListener("pointermove", onPointerMove);
  window.removeEventListener("pointerup", onPointerUp);
  window.removeEventListener("pointercancel", onCancel);
  window.removeEventListener("keydown", onKeyDown);
  window.removeEventListener("blur", onCancel);
  document.removeEventListener("visibilitychange", onVisibility);

  if (!current) {
    return;
  }

  if (current.frameId !== null) {
    cancelAnimationFrame(current.frameId);
  }

  if (current.dragging) {
    hideLayer();
    attachedTo?.classList.remove("slot-dragging");

    if (current.originRect) {
      current.originRect.style.opacity = current.originOpacity;
    }

    document.body.style.userSelect = bodyUserSelect;

    try {
      if (current.pointerId !== undefined) {
        attachedTo?.releasePointerCapture?.(current.pointerId);
      }
    } catch {
      // already released
    }

    host?.onDragEnd?.();
  }
};

function onCancel() {
  end();
}

function onVisibility() {
  if (document.hidden) {
    end();
  }
}

function onKeyDown(event: KeyboardEvent) {
  if (event.key === "Escape" && state?.dragging) {
    event.preventDefault();
    end();
  }
}

const beginDrag = (event: PointerEvent) => {
  if (!state || !host || !attachedTo) {
    return;
  }

  if (!host.allowed()) {
    end();

    return;
  }

  const boxes = host.boxes();
  const origin = boxes.find((b) => b.slot === state!.slot);

  if (!origin) {
    end();

    return;
  }

  ensureLayer(attachedTo);

  const point = toSvgPoint(attachedTo, state.startX, state.startY);

  state.boxes = boxes;
  state.zone = host.removeZone?.() ?? null;
  state.inRemove = false;

  if (state.zone) {
    placeRemoveHint(state.zone);
  }

  state.offsetX = point.x - origin.x;
  state.offsetY = point.y - origin.y;
  state.targetSlot = state.slot;
  state.plan = { kind: "none" };
  state.dragging = true;

  ghostRect!.setAttribute("width", String(origin.width));
  ghostRect!.setAttribute("height", String(origin.height));
  ghostRect!.setAttribute("stroke", ACCENT);
  ghostRect!.removeAttribute("stroke-dasharray");
  ghostLabel!.setAttribute("x", String(origin.width / 2));
  ghostLabel!.setAttribute("y", String(origin.height / 2 + 3));
  ghostLabel!.textContent = host.label(state.slot);
  ghostReason!.setAttribute("x", String(origin.width / 2));
  ghostReason!.setAttribute("y", String(origin.height + 15));
  ghostReason!.textContent = "";
  ghost!.setAttribute("display", "");
  highlight!.setAttribute("display", "none");

  // the box the page is being lifted from dims, and stays dimmed until the drop
  state.originRect = attachedTo.querySelector(`rect[id='rect_${state.slot}']`) as SVGElement | null;
  state.originOpacity = state.originRect?.style.opacity ?? "";

  if (state.originRect) {
    state.originRect.style.opacity = "0.35";
  }

  attachedTo.classList.add("slot-dragging");
  bodyUserSelect = document.body.style.userSelect;
  document.body.style.userSelect = "none";

  try {
    if (event.pointerId !== undefined) {
      attachedTo.setPointerCapture?.(event.pointerId);
    }
  } catch {
    // some environments refuse capture; the window listeners still receive the moves
  }

  host.onDragStart?.();
  schedule();
};

function onPointerMove(event: PointerEvent) {
  if (!state || event.pointerId !== state.pointerId) {
    return;
  }

  state.lastX = event.clientX;
  state.lastY = event.clientY;

  if (!state.dragging) {
    if (Math.hypot(event.clientX - state.startX, event.clientY - state.startY) < DRAG_THRESHOLD_PX) {
      return;
    }

    beginDrag(event);

    return;
  }

  schedule();
}

function onPointerUp(event: PointerEvent) {
  if (!state || event.pointerId !== state.pointerId) {
    return;
  }

  const finished = state;

  // make sure the last position is the one the plan was computed for
  if (finished.dragging) {
    finished.lastX = event.clientX;
    finished.lastY = event.clientY;

    if (finished.frameId !== null) {
      cancelAnimationFrame(finished.frameId);
    }

    finished.framePending = false;
    finished.frameId = null;
    update();
  }

  const { dragging, plan, inRemove, slot } = finished;

  end();

  if (!dragging) {
    return; // a plain click: nothing of ours, the box's own click handler runs
  }

  swallowNextClick();

  if (inRemove) {
    host?.remove?.(slot);

    return;
  }

  if (plan && (plan.kind === "move" || plan.kind === "swap")) {
    host?.commit(plan.changes, plan.kind);
  }
}

const onPointerDown = (event: PointerEvent) => {
  if (!host || event.button !== 0 || event.isPrimary === false) {
    return;
  }

  const hit = rectOf(event.target);

  if (!hit || !canDrag(hit.slot, hit.selected)) {
    return;
  }

  end(); // a stale gesture, if any

  state = {
    pointerId: event.pointerId,
    startX: event.clientX,
    startY: event.clientY,
    lastX: event.clientX,
    lastY: event.clientY,
    slot: hit.slot,
    dragging: false,
    boxes: [],
    offsetX: 0,
    offsetY: 0,
    targetSlot: null,
    plan: null,
    framePending: false,
    frameId: null,
    originRect: null,
    originOpacity: "",
    zone: null,
    inRemove: false,
  };

  window.addEventListener("pointermove", onPointerMove);
  window.addEventListener("pointerup", onPointerUp);
  window.addEventListener("pointercancel", onCancel);
  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("blur", onCancel);
  document.addEventListener("visibilitychange", onVisibility);
};

// "grab" on a box that can be dragged, nothing otherwise. Evaluated on ENTERING a box (pointerover), not on every move.
const onPointerOver = (event: PointerEvent) => {
  const hit = rectOf(event.target);

  if (!hit) {
    return;
  }

  (hit.rect as SVGElement).style.cursor = canDrag(hit.slot, hit.selected) ? "grab" : "";
};

// Wire the drag to the grid's <svg>. Safe to call again when the svg is rebuilt (createStaticComponents): the old wiring is dropped first.
export const attachSlotDrag = (slotDragHost: SlotDragHost): (() => void) => {
  detachSlotDrag();

  const svgNode = slotDragHost.svgNode();

  if (!svgNode) {
    return () => undefined;
  }

  host = slotDragHost;
  attachedTo = svgNode;
  svgNode.addEventListener("pointerdown", onPointerDown as EventListener);
  svgNode.addEventListener("pointerover", onPointerOver as EventListener);

  detachListeners = () => {
    svgNode.removeEventListener("pointerdown", onPointerDown as EventListener);
    svgNode.removeEventListener("pointerover", onPointerOver as EventListener);
  };

  return detachSlotDrag;
};

export const detachSlotDrag = () => {
  end();
  detachListeners?.();
  detachListeners = null;
  layer?.remove();
  layer = highlight = ghost = ghostRect = ghostLabel = ghostReason = removeHint = removeLine = removeIcon = removeText = null;
  host = null;
  attachedTo = null;
};
